import React from 'react';
import { Link } from 'react-router-dom';
import './BusinessSplash.css';

const DEFAULT_COPY = {
  heroTitle: 'One Calendar. One Workflow. One Source of Truth.',
  heroSubtitle: 'Real-Time Operations for Service Teams',
  heroBody: 'Caltronic.Pro aligns scheduling, dispatch, billing, and customer communication in one live system so your office moves faster with fewer mistakes.',
  managementByColorTitle: 'Operational Clarity by Color',
  managementByColorBody: 'At a glance, your team knows what needs action now.',
  managementByColorFocus: [
    'See schedule updates instantly across the team.',
    'Spot payment-ready and overdue jobs at a glance.',
    'Keep priorities clear during busy days.'
  ],
  managementByColorStatuses: [
    'Green: Scheduled and ready to run.',
    'Yellow: Updated and synced.',
    'Blue: Completed, invoiced, and documented.',
    'Red: Canceled with clear history.'
  ],
  visualDispatch: [
    'Validated addresses reduce wrong-location trips.',
    'Live status helps dispatch make faster calls.',
    'Auto confirmations reduce manual follow-up.',
    'Branded invoice and receipt emails include PDFs.'
  ],
  realTimeSyncTitle: 'Real-Time Office and Field Sync',
  realTimeSyncBody: 'Shared real-time status keeps office and field aligned without manual handoffs.',
  realTimeSyncPoints: [
    'One live schedule for dispatch, field, and office',
    'Instant visibility for reassigned, completed, and paid work',
    'Fewer status check-ins during high-volume periods',
    'One source of truth for operations and billing'
  ],
  partners: [
    'HVAC teams',
    'Septic service teams',
    'Plumbing service teams',
    'General contractors',
    'Commercial facility teams',
    'Property managers'
  ],
  technicianCalloutTitle: 'Technician Update, Office Moves',
  technicianCalloutBody: 'One field action drives billing and communication automatically.',
  technicianCalloutPoints: [
    'Completion updates the office immediately.',
    'Service history is captured once, accurately.',
    'Dispatch and billing stay aligned.'
  ],
  technicianTrigger: 'One click triggers invoice delivery, PDF attachments, payment link, and status sync.',
  pilotTitle: '30-Day Pilot, Risk Controlled',
  primaryCtaLabel: 'Start 30-Day Pilot',
  secondaryCtaLabel: 'Client Log In',
  pricingTitle: 'Founder Plan Pricing',
  pricingPlanName: 'Founder Plan: $129/month per office',
  pricingAnnual: 'Annual option: $1,290 (about two months free equivalent)',
  pricingIncludes: [
    'Full workflow: scheduling, dispatch, invoicing, communication, and reporting.',
    'Start with a 30-day pilot.',
    'Founder lock: first 15 customers keep $129 for 12 months.'
  ],
  onboardingTitle: 'Onboarding and Activation',
  onboardingPolicy: [
    'Standard onboarding: $299 one-time',
    'Founding promotion: onboarding waived for first 10 signed customers',
    'Stripe onboarding is central to activation and is prioritized in implementation'
  ],
  pricingNarrativeTitle: 'Why This Price',
  pricingNarrative: [
    'Caltronic Pro replaces manual handoffs between office, dispatch, billing, and customer communication.',
    'Most teams recover value quickly through fewer missed follow-ups and faster collections.',
    'Founder pricing rewards early partners while we scale.'
  ],
  roiAnchor: 'If you recover just 3-5 hours/week of office coordination plus faster invoice collection, the software pays for itself quickly.',
  pricingRuleTitle: 'Pricing Rule',
  pricingRule: [
    'If close rate is high and churn is low for 6-8 weeks, raise to $149.',
    'If close rate is weak, keep $129 and improve onboarding + proof first.',
    'Do not add tiers until 10+ paying customers and repeated feature segmentation requests.'
  ],
  financials: [
    'Generate invoices the moment work is completed.',
    'Deliver a professional, consistent billing flow.',
    'Reconcile payments with less manual entry.',
    'Improve collections and cash flow.'
  ],
  reporting: [
    'Track customers, unpaid invoices, and revenue by date.',
    'Export CSV service history for audits and accounting.',
    'Catch payment gaps and workload trends earlier.',
    'Keep records your office can trust.'
  ],
  technology: [
    'Fast React frontend for responsive daily use.',
    'Secure Node.js backend with role-based JWT access.',
    'Structured, audit-ready scheduling and billing data.',
    'Stripe integration for secure payments and reporting.',
    'Address and route intelligence built in.'
  ],
  costOfInaction: [
    'More time lost to status check-ins.',
    'Recurring manual billing effort.',
    'Address errors that increase mileage and delays.',
    'Slower payment cycles that strain cash flow.'
  ],
  pilot: [
    'Priority onboarding window.',
    'Clear job timeline tracking.',
    'Instant Stripe invoicing workflow.',
    'End-of-month operational report.',
    'Full visibility for your office.'
  ],
  pilotGuaranteeTitle: 'Pilot Guarantees',
  pilotGuarantees: [
    '14-Day Go-Live Guarantee with pilot credit protection.',
    'Same-business-day onboarding response commitment.',
    'No-surprise pricing when scope is unchanged.'
  ],
  serviceArea: 'Continental United States.',
  supportHours: 'Monday-Friday, 8:00 AM-5:00 PM EST, excluding holidays.',
  emergencyPolicy: 'Emergency calls are accepted and expected within standard hours.'
};

// Change these numbers to reorder body panels without moving JSX blocks.
const PANEL_ORDER = {
  solution: 10,
  realTimeSync: 15,
  reporting: 20,
  whyCaltronic: 30,
  managementByColor: 70,
  technicianCallout: 80,
  pilot: 82,
  technology: 85,
  financials: 90,
  costOfInaction: 100,
  partners: 102,
  serviceVerticals: 103,
  pricing: 105,
  supportPolicy: 120
};

function BusinessSplash() {
  const getStatusTone = (item) => {
    if (item.startsWith('Green:')) return 'green';
    if (item.startsWith('Yellow:')) return 'yellow';
    if (item.startsWith('Blue:')) return 'blue';
    if (item.startsWith('Red:')) return 'red';
    if (item.startsWith('Paid:')) return 'paid';
    if (item.startsWith('Unpaid:')) return 'unpaid';
    return 'neutral';
  };

  const splitStatus = (item) => {
    const parts = item.split(':');
    if (parts.length < 2) {
      return { label: item, detail: '' };
    }
    const label = parts.shift();
    return { label, detail: parts.join(':').trim() };
  };

  return (
    <section className="business-splash" aria-label="Business information">
      <div className="business-splash__card">
        <div className="business-splash__hero">
          <div className="business-splash__intro">
            <div className="business-splash__lede-box">
              <h1 className="business-splash__eyebrow">REAL-TIME OPERATIONS FOR SERVICE TEAMS</h1>
              <p className="business-splash__lede">
                {DEFAULT_COPY.heroTitle}
              </p>
              <p className="business-splash__hero-body">{DEFAULT_COPY.heroBody}</p>
            </div>
            <div className="business-splash__cta-row business-splash__cta-row--banner" role="group" aria-label="Primary actions">
              <Link className="business-splash__cta business-splash__cta--primary" to="/request-access">
                {DEFAULT_COPY.primaryCtaLabel}
              </Link>
              <Link className="business-splash__cta business-splash__cta--secondary" to="/login">
                {DEFAULT_COPY.secondaryCtaLabel}
              </Link>
            </div>
          </div>
        </div>

        <div className="business-splash__grid">
          <article className="business-splash__panel" style={{ order: PANEL_ORDER.solution }}>
            <h2>The Caltronic.Pro Solution</h2>
            <p>
              Caltronic.Pro brings scheduling, dispatch visibility, billing, and customer operations into one trusted workflow.
              It is purpose-built for office teams that need speed, accountability, and clean records. The app also communicates
              with customers across the full journey, from a welcome message to a friendly invoice and receipt email with PDF
              attachments.
            </p>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.pricing }}>
            <h2>{DEFAULT_COPY.pricingTitle}</h2>
            <p><strong>{DEFAULT_COPY.pricingPlanName}</strong></p>
            <p>{DEFAULT_COPY.pricingAnnual}</p>
            <ul>
              {DEFAULT_COPY.pricingIncludes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.managementByColor }}>
            <h2>{DEFAULT_COPY.managementByColorTitle}</h2>
            <p>{DEFAULT_COPY.managementByColorBody}</p>
            <ul>
              {DEFAULT_COPY.managementByColorFocus.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <ul className="business-splash__status-list" aria-label="Color status legend">
              <li className="business-splash__status-item business-splash__status-item--payment-row">
                <strong>Payment:</strong>
                <span className="business-splash__payment-text business-splash__payment-text--paid">Paid</span>
                <span>/</span>
                <span className="business-splash__payment-text business-splash__payment-text--unpaid">Unpaid</span>
              </li>
              {DEFAULT_COPY.managementByColorStatuses.map((item) => {
                const tone = getStatusTone(item);
                const { label, detail } = splitStatus(item);

                return (
                  <li
                    key={item}
                    className={`business-splash__status-item business-splash__status-item--${tone}`}
                  >
                    <strong className={`business-splash__status-label business-splash__status-label--${tone}`}>{label}:</strong> {detail}
                  </li>
                );
              })}
            </ul>
            <img
              className="business-splash__management-image"
              src="/splash-calendar-preview.png"
              alt="Calendar view with color-coded events for management by color"
              loading="lazy"
            />
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.realTimeSync }}>
            <h2>{DEFAULT_COPY.realTimeSyncTitle}</h2>
            <p>{DEFAULT_COPY.realTimeSyncBody}</p>
            <ul>
              {DEFAULT_COPY.realTimeSyncPoints.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.whyCaltronic }}>
            <h2>Why Caltronic.Pro</h2>
            <ul>
              {DEFAULT_COPY.visualDispatch.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel business-splash__panel--callout" style={{ order: PANEL_ORDER.technicianCallout }}>
            <h2>{DEFAULT_COPY.technicianCalloutTitle}</h2>
            <p>{DEFAULT_COPY.technicianCalloutBody}</p>
            <ul>
              {DEFAULT_COPY.technicianCalloutPoints.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <img
              className="business-splash__management-image"
              src="/splash-event-preview.png"
              alt="Event modal showing title, schedule, pricing, payment status, and customer details"
              loading="lazy"
            />
            <p className="business-splash__trigger-note">
              <span className="business-splash__invoice-emphasis">Green Send Invoice button</span>
              : {DEFAULT_COPY.technicianTrigger}
            </p>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.financials }}>
            <h2>One-Click Financials with Stripe</h2>
            <ul>
              {DEFAULT_COPY.financials.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.reporting }}>
            <h2>Reporting and Compliance</h2>
            <ul>
              {DEFAULT_COPY.reporting.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.technology }}>
            <h2>Technology Behind the Reliability</h2>
            <ul>
              {DEFAULT_COPY.technology.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.costOfInaction }}>
            <h2>The Cost of Inaction</h2>
            <ul>
              {DEFAULT_COPY.costOfInaction.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.pilot }}>
            <h2>{DEFAULT_COPY.pilotTitle}</h2>
            <ul>
              {DEFAULT_COPY.pilot.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.serviceVerticals }}>
            <h2>Service Verticals</h2>
            <p><strong>HVAC:</strong> Dispatch, billing, and service history in one real-time workflow for HVAC office and field teams.</p>
            <p><strong>Septic:</strong> Schedule route-heavy septic jobs with live status updates and faster payment collection.</p>
            <p><strong>Plumbing:</strong> Keep plumbing service calls, invoicing, and customer communication aligned from one calendar.</p>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.partners }}>
            <h2>Built for Partners and Service Verticals</h2>
            <ul>
              {DEFAULT_COPY.partners.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="business-splash__panel" style={{ order: PANEL_ORDER.supportPolicy }}>
            <h2>Service Area and Support Policy</h2>
            <dl>
              <div className="business-splash__kv-row">
                <dt>Service Area</dt>
                <dd>{DEFAULT_COPY.serviceArea}</dd>
              </div>
              <div className="business-splash__kv-row">
                <dt>Support Hours</dt>
                <dd>{DEFAULT_COPY.supportHours}</dd>
              </div>
              <div className="business-splash__kv-row">
                <dt>Emergency Policy</dt>
                <dd>{DEFAULT_COPY.emergencyPolicy}</dd>
              </div>
            </dl>
          </article>
        </div>

        <section className="business-splash__banner" aria-label="Call to action">
          <h2>Ready to Modernize Your Operations?</h2>
          <p>
            Caltronic.Pro helps turn service businesses into digital-first, audit-ready, real-time operations.
            Let us build your 30-day pilot.
          </p>
          <div className="business-splash__cta-row business-splash__cta-row--banner" role="group" aria-label="Call to action buttons">
            <Link className="business-splash__cta business-splash__cta--primary" to="/request-access">
              {DEFAULT_COPY.primaryCtaLabel}
            </Link>
            <Link className="business-splash__cta business-splash__cta--secondary" to="/login">
              {DEFAULT_COPY.secondaryCtaLabel}
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default BusinessSplash;
